import React from 'react'
import {Document, Image, Page, Text, View} from '@react-pdf/renderer'
import moment from 'moment'
import ThaiBahtText from 'thai-baht-text'
import LDCLogo from '../../../../_metronic/assets/ldc-logo.png'
import {IIncomeDocument} from '../IncomeDocumentDetailPage'

/**
 * หน้าพิมพ์ใบรวมจ่าย (CPN)
 *
 * เดิมใช้แบบฟอร์มกลางของเอกสารฝั่งขาย: "ผู้ขาย" เป็นบริษัทเอง คู่ค้าไปอยู่ช่อง "ลูกค้า" มี "พนักงานขาย"
 * และตารางเป็นรายการสินค้าข้างใน AP/RR — ลูกค้าบอกว่า "พิมพ์ออกมาเป็นอะไรก็ไม่รู้" (30 ก.ย. 2569)
 * ใบรวมจ่ายคือใบสรุปเอกสารตั้งหนี้ที่จะจ่ายผู้ขายรายเดียว จึงแสดงรายการเอกสาร AP/RR แทน
 *
 * ยอดต่อเอกสารมาจาก `childrenDocumentsJson` ที่บันทึกไว้ตอนสร้าง (ยอดวางบิลตามที่เลือก) ส่วนยอดรวม
 * ใช้ยอดของใบรวมจ่ายจาก API — มัดจำที่นำมาหัก (await-deduct) และใบลดหนี้ลดยอด เหมือนตารางบนหน้าจอ
 */

const COMPANY = {
  name: 'บริษัท แอลดีซี เด็นทัล จำกัด (มหาชน)',
  address: '395-395/1 อาคารแอลดีซี ม.5 ถ.ศรีนครินทร์ ต.สำโรงเหนือ อ.เมืองสมุทรปราการ จ.สมุทรปราการ 10270',
  taxId: '0107557000080 (สำนักงานใหญ่)',
}

const TYPE_LABEL: Record<string, string> = {
  'expense-expense-record': 'บันทึกค่าใช้จ่าย',
  'expense-purchase-inventory-record': 'บันทึกซื้อสินค้า',
  'expense-deposit-payment': 'หักเงินมัดจำ',
  'expense-purchase-credit-note': 'ใบลดหนี้',
  'expense-purchase-debit-note': 'ใบเพิ่มหนี้',
}

const money = (value: number) =>
  Number(value || 0).toLocaleString('th-TH', {minimumFractionDigits: 2, maximumFractionDigits: 2})

const parseChildren = (value: unknown): any[] => {
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

/** มัดจำที่นำมาหักและใบลดหนี้ลดยอด (เหมือน SelectorTable) */
const isDeduction = (doc: any) =>
  doc?.state === 'await-deduct' || doc?.type === 'expense-purchase-credit-note'

const COLUMNS = [
  {title: '#', width: '5%', align: 'center' as const},
  {title: 'เลขที่เอกสาร', width: '17%'},
  {title: 'ประเภท', width: '15%'},
  {title: 'วันที่', width: '11%'},
  {title: 'เลขที่ใบกำกับภาษี', width: '16%'},
  {title: 'ภาษีมูลค่าเพิ่ม', width: '11%', align: 'right' as const},
  {title: 'หัก ณ ที่จ่าย', width: '11%', align: 'right' as const},
  {title: 'ยอดวางบิล', width: '14%', align: 'right' as const},
]

const CombinedPaymentNotePDF: React.FC<{document: IIncomeDocument; pageStyle: any}> = ({document, pageStyle}) => {
  const doc: any = document
  const supplier = doc.labCompany || {}
  const children = parseChildren(doc.childrenDocumentsJson)
  const totalAmount = Number(doc.totalAmount || 0)
  const whTaxPrice = Number(doc.whTaxPrice || 0)
  const payable = totalAmount - whTaxPrice

  const infoRow = (label: string, value: any, labelWidth = 80) => (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Text style={{fontWeight: 'bold', width: labelWidth}}>{`${label} :`}</Text>
      <Text style={{flex: 1}}>{value || '-'}</Text>
    </View>
  )

  const cell = (text: any, index: number, bold = false) => (
    <Text
      key={index}
      style={{
        width: COLUMNS[index].width,
        textAlign: COLUMNS[index].align || 'left',
        paddingHorizontal: 3,
        ...(bold ? {fontWeight: 'bold'} : {}),
      }}
    >
      {text}
    </Text>
  )

  const signatureBox = (title: string, name?: string) => (
    <View style={{flex: 1, textAlign: 'center', paddingHorizontal: 6}}>
      <Text>{title}</Text>
      <Text style={{marginTop: 28, color: '#616161'}}>{'------------------------------'}</Text>
      <Text>{name ? `( ${name} )` : '(                                        )'}</Text>
      <Text>{'วันที่ ......../......../........'}</Text>
    </View>
  )

  const pages = [
    {key: 'original', label: '( ต้นฉบับ / original )'},
    {key: 'copy', label: '( สำเนา / copy )'},
  ]

  return (
    <Document title={`LDC-${doc.code}`}>
      {pages.map((page) => (
        <Page key={page.key} size='A4' style={pageStyle}>
          {/* หัวเอกสาร */}
          <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
            <Image src={LDCLogo} style={{width: 100, height: 'auto'}} />
            <View style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
              <Text style={{fontSize: 28, fontWeight: 'bold', color: '#3f51b5'}}>ใบรวมจ่าย</Text>
              <Text style={{fontSize: 14, color: '#4c4c4c', marginTop: -6, marginBottom: 10}}>{page.label}</Text>
            </View>
          </View>

          {/* บริษัท / ผู้ขาย / เลขที่เอกสาร */}
          <View style={{display: 'flex', flexDirection: 'row', gap: '1rem'}}>
            <View style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '1px'}}>
              {infoRow('ผู้จ่ายเงิน', COMPANY.name)}
              {infoRow('ที่อยู่', COMPANY.address)}
              {infoRow('เลขประจำตัวผู้เสียภาษี', COMPANY.taxId, 110)}
              <View style={{width: '100%', margin: '8px 0', borderTop: '1px solid #bfbfbf'}} />
              {infoRow('ผู้ขาย/เจ้าหนี้', supplier.name)}
              {infoRow('รหัสผู้ขาย', supplier.code)}
              {infoRow('ที่อยู่', supplier.fullAddress)}
              {infoRow('เลขประจำตัวผู้เสียภาษี', supplier.taxId, 110)}
            </View>
            <View style={{width: '33%'}}>
              <View style={{padding: '4px 8px', backgroundColor: '#e4e5fc', borderRadius: '.25rem'}}>
                {infoRow('เลขที่เอกสาร', doc.code, 70)}
                {infoRow('วันที่ออก', doc.date ? moment(doc.date).format('DD/MM/YYYY') : '-', 70)}
                {infoRow('ครบกำหนด', doc.expiryDate ? moment(doc.expiryDate).format('DD/MM/YYYY') : '-', 70)}
                {infoRow('จำนวนเอกสาร', `${children.length} รายการ`, 70)}
              </View>
            </View>
          </View>

          {/* รายการเอกสารที่รวมจ่าย */}
          <View style={{marginTop: 12}}>
            <View style={{display: 'flex', flexDirection: 'row', backgroundColor: '#E0E7FF', paddingVertical: 6, borderRadius: '4px'}}>
              {COLUMNS.map((column, index) => cell(column.title, index, true))}
            </View>
            {children.map((child, rowIndex) => {
              const sign = isDeduction(child) ? -1 : 1
              return (
                <View
                  key={child.id || rowIndex}
                  wrap={false}
                  style={{display: 'flex', flexDirection: 'row', paddingVertical: 5, borderBottom: '1px solid #e0e0e0'}}
                >
                  {cell(rowIndex + 1, 0)}
                  {cell(child.code, 1)}
                  {cell(TYPE_LABEL[child.type] || '-', 2)}
                  {cell(child.date ? moment(child.date).format('DD/MM/YYYY') : '-', 3)}
                  {cell(child.taxCode || '-', 4)}
                  {cell(money(sign * Number(child.vatPrice || 0)), 5)}
                  {cell(money(sign * Number(child.whTaxPrice || 0)), 6)}
                  {cell(money(sign * Number(child.billAmount ?? child.totalAmount ?? 0)), 7)}
                </View>
              )
            })}
          </View>

          {/* สรุปยอด — ยอดของใบรวมจ่ายจาก API */}
          <View style={{display: 'flex', flexDirection: 'row', marginTop: 10}}>
            <View style={{flex: 1, paddingRight: 12}}>
              {!!doc.note && infoRow('หมายเหตุ', doc.note, 50)}
              <View style={{marginTop: 6, padding: '4px 8px', backgroundColor: '#F5F5F5'}}>
                <Text style={{fontWeight: 'bold'}}>{`(${ThaiBahtText(payable)})`}</Text>
              </View>
            </View>
            <View style={{width: '38%'}}>
              {[
                ['ยอดรวมทั้งสิ้น', totalAmount],
                ['หัก ณ ที่จ่าย', whTaxPrice],
              ].map(([label, value]) => (
                <View key={String(label)} style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2}}>
                  <Text>{String(label)}</Text>
                  <Text>{`${money(Number(value))} บาท`}</Text>
                </View>
              ))}
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingVertical: 4,
                  marginTop: 2,
                  borderTop: '1px solid #bfbfbf',
                }}
              >
                <Text style={{fontWeight: 'bold'}}>ยอดชำระสุทธิ</Text>
                <Text style={{fontWeight: 'bold'}}>{`${money(payable)} บาท`}</Text>
              </View>
            </View>
          </View>

          {/* ลายเซ็น */}
          <View style={{display: 'flex', flexDirection: 'row', marginTop: 'auto', paddingTop: 24, paddingBottom: 12}}>
            {signatureBox('ผู้จัดทำ', doc.actionBy?.fullName)}
            {signatureBox('ผู้อนุมัติ', doc.approver?.fullName)}
            {signatureBox('ผู้รับเงิน', supplier.name)}
          </View>
        </Page>
      ))}
    </Document>
  )
}

export default CombinedPaymentNotePDF
